import React from 'react';

import { Box, Tab, Tabs } from 'components';
import { useIsMobile, usePlanFeature } from 'hooks';

import { UsageBillingTab } from './Billing/UsageBillingTab';
import { EmailDesigner } from './EmailDesigner/EmailDesigner';
import { GeneralSettings } from './GeneralSettings';

import {
  useNavigate,
  useParams,
  useSearchParams,
  useTranslation,
} from 'third-party';

import { BILLING_ENABLED } from 'constants/common';
import { settingsTabUrl } from 'constants/url';

import { PlanFeature, SettingsTab } from 'types/app';

export const Settings = () => {
  const { organizationId = '', tab } = useParams<{
    organizationId: string;
    tab: SettingsTab;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const planIncludes = usePlanFeature();

  const tabs = [
    {
      id: SettingsTab.General,
      label: t('settings.tab'),
      render: GeneralSettings,
    },
    ...(BILLING_ENABLED
      ? [
          {
            id: SettingsTab.Billing,
            label: t('billing.tab'),
            render: UsageBillingTab,
          },
        ]
      : []),
    ...(isMobile || !planIncludes(PlanFeature.CustomEmailTemplates)
      ? []
      : [
          {
            id: SettingsTab.EmailDesigner,
            label: t('emailDesigner.tab'),
            render: EmailDesigner,
          },
        ]),
  ];

  // The tab lives in the URL so Stripe can return the customer straight to billing, and so a tab
  // survives a refresh. An unknown or mobile-hidden tab falls back to the first one.
  const activeIndex = Math.max(
    0,
    tabs.findIndex(it => it.id === tab),
  );
  const ActiveTabContent = tabs[activeIndex].render;

  const onTabChange = (index: number) => {
    const query = searchParams.toString();
    navigate(
      `${settingsTabUrl(organizationId, tabs[index].id)}${
        query ? `?${query}` : ''
      }`,
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Tabs
          value={activeIndex}
          onChange={(_event, value) => onTabChange(value)}
        >
          {tabs.map((it, index) => (
            <Tab
              key={it.id}
              label={it.label}
              id={`settings-tab-${index}`}
              aria-controls={`settings-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      <Box
        role="tabpanel"
        id={`settings-tabpanel-${activeIndex}`}
        aria-labelledby={`settings-tab-${activeIndex}`}
        sx={{ p: 3, flexGrow: 1, minHeight: 0, overflow: 'auto' }}
      >
        <ActiveTabContent />
      </Box>
    </Box>
  );
};
